import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useSessionContext } from "@/contexts/SessionContext";
import { useTeleconsultoriaContractContext } from "@/contexts/TeleconsultoriaContractContext";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import { organizationTokenFromId } from "../services/teleconsultoriaScheduleService";
import {
  emptyProfile,
  resolveTeleconsultoriaProfile,
} from "./services/resolveTeleconsultoriaProfile";
import type { TeleconsultoriaProfile, TeleconsultoriaProfileState } from "./types";

type ResolvedProfile = { profile: TeleconsultoriaProfile; errorMessage?: string };

/** Referência estável (initialData do useAsyncTask). */
const EMPTY_RESOLVED: ResolvedProfile = { profile: emptyProfile() };

/** Permissões derivadas do perfil: uma regra só, usada em todas as telas. */
function deriveState(
  resolved: ResolvedProfile,
  isLoading: boolean,
  reload: () => void,
): TeleconsultoriaProfileState {
  const { profile } = resolved;
  // "unassigned" (extensão sem relação) não convida, não fica disponível e não vê teleconsultores.
  const hasRelation = profile.kind === "provider" || profile.kind === "affiliate";
  return {
    profile,
    isLoading,
    errorMessage: resolved.errorMessage,
    canInviteTeleconsultor: hasRelation,
    canManageAvailability: profile.kind === "provider",
    // Afiliada: só a prestadora dela. Prestadora: só a própria organização.
    teleconsultorOrganizationTokens:
      !hasRelation || !profile.providerToken ? [] : [profile.providerToken],
    reload,
  };
}

const TeleconsultoriaProfileContext = createContext<TeleconsultoriaProfileState>(
  deriveState(EMPTY_RESOLVED, false, () => undefined),
);

/**
 * Resolve o perfil (prestadora / afiliada / sem relação / nenhum) UMA vez por organização
 * e contrato. Fora da visão profissional, o perfil é sempre "nenhum".
 */
export function TeleconsultoriaProfileProvider({ children }: { children: ReactNode }) {
  const { access } = useSessionContext();
  const { contractTeleconsultoria, loading: isContractLoading } =
    useTeleconsultoriaContractContext();
  const isProfessional = access?.type === "professional";
  const organizationToken = isProfessional
    ? organizationTokenFromId(access?.organizationId)
    : "";
  const hasContract = Boolean(contractTeleconsultoria);

  const resolveTask = useAsyncTask({
    task: useCallback(
      () => resolveTeleconsultoriaProfile({ organizationToken, hasContract }),
      [organizationToken, hasContract],
    ),
    initialData: EMPTY_RESOLVED,
  });

  const { run, reset } = resolveTask;
  const reload = useCallback(() => {
    void run().catch(() => undefined);
  }, [run]);

  useEffect(() => {
    // Espera o contrato carregar para não decidir o perfil com dado parcial.
    if (isContractLoading) return;
    if (!organizationToken) {
      reset();
      return;
    }
    reload();
  }, [organizationToken, isContractLoading, reload, reset]);

  const value = useMemo(() => {
    const resolved = resolveTask.data ?? EMPTY_RESOLVED;
    // Perfil de outra organização (troca de clínica ou resposta atrasada) não vale aqui.
    const isCurrentOrganization =
      resolved.profile.organizationToken === organizationToken;
    return deriveState(
      isCurrentOrganization ? resolved : { profile: emptyProfile(organizationToken) },
      resolveTask.loading || isContractLoading,
      reload,
    );
  }, [resolveTask.data, resolveTask.loading, isContractLoading, organizationToken, reload]);

  return (
    <TeleconsultoriaProfileContext.Provider value={value}>
      {children}
    </TeleconsultoriaProfileContext.Provider>
  );
}

export function useTeleconsultoriaProfile(): TeleconsultoriaProfileState {
  return useContext(TeleconsultoriaProfileContext);
}
